import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    styled
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import {
    confirmProfile,
    resetUpdatingStatus,
    retrieveBasicInfo,
    retrieveContactRelations,
    retrieveCountries,
    retrieveGroupsForBasicDetails,
    retrieveProfile,
    updateBasicInfo,
    updateStudentByParent
} from '../../ProfilesSlice';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import ProfileStatus from '../../Components/ProfileStatus';
import Typography from '@mui/material/Typography';
import {useParams} from 'react-router-dom';
import {useConfirm} from 'material-ui-confirm';
import useUser from '../../../../../utility/hooks/useUser';
import {formatDate} from '../../../../../utility/dateUtil';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import PropTypes from 'prop-types';
import {isValidUPN, validateEmail, validateFieldLength} from '../../../../../utility/validationUtil';
import useTextFields from '../../../../../utility/hooks/useTextFields';
import moment from 'moment/moment';
import {EditableFields} from '../../../../../shared-components/EditableFields';
import AdmicityAddressLookup from '../../../../../shared-components/AdmicityAddressLookup';
import PROFILE_TYPE from '../../../../../constants/profileTypes';
import AdmissionCategories from './AdmissionCategories';
import ApplicantPhotoUpload from './ApplicantPhotoUpload';

const ActionButtonsBox = styled(Box)(({theme}) => ({
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'end',
    paddingTop: theme.spacing(2),
}));

const checkTextFieldsAreValid = fields =>
    !Object.values(fields)
        .some(({textFieldProps: props}) => props.error || ((props.value === '' || props.value === null || props.value === undefined) && props.required));

const BasicInfo = ({readonly}) => {
    const {user} = useUser();
    const [isEditMode, setIsEditMode] = useState(false);
    const {studentId} = useParams();
    const {
        selectedProfile,
        updatingSuccessful,
        houseGroups,
        yearGroups,
        formGroups,
        boarderGroups,
        currentSchool
    } = useSelector(state => state.profilesInfo);
    const dispatch = useDispatch();
    const confirm = useConfirm();
    const profileData = selectedProfile.basicInfo;
    const userHasParentRole = user.isParent();
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [addressUpdateMode, setAddressUpdateMode] = useState('all');
    const [addressComponentKey, setAddressComponentKey] = useState(0);
    const [pendingPhoto, setPendingPhoto] = useState(null);

    const canViewPermissionModule = (permissionModule) => {
        if (user.permissions.includes(`Permission.${permissionModule}.Edit`)) {
            return true;
        }

        if (selectedProfile.isParentOfStudent &&
            user.parentPermissions &&
            user.parentPermissions.includes(`Permission.${permissionModule}.Edit`)) {
            return true;
        }

        if (user.permissions.includes(`Permission.${permissionModule}.View`)) {
            return true;
        }

        return selectedProfile.isParentOfStudent &&
            user.parentPermissions &&
            user.parentPermissions.includes(`Permission.${permissionModule}.View`);
    };

    const profileAddress = useMemo(() => {
        if (!profileData) return null;

        const addressFields = ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode', 'country', 'countryCode'];
        const address = {};

        addressFields.forEach(field => {
            if (profileData[field] !== undefined && profileData[field] !== null && profileData[field] !== '') {
                address[field] = profileData[field];
            }
        });

        return Object.keys(address).length > 0 ? address : null;
    }, [profileData]);

    const [originalAddress, setOriginalAddress] = useState(null);

    const studentInfoTextFields = useMemo(() => {
        const dateOfBirth = profileData.dateOfBirth ? moment(profileData.dateOfBirth) : null;

        return profileData
            ? [
                {
                    id: 'profile_forename',
                    initialValue: profileData.forename,
                    label: 'Forename',
                    name: 'forename',
                    required: true,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60),
                },
                {
                    id: 'profile_middleName',
                    initialValue: profileData.middleName,
                    label: 'Middle Name',
                    name: 'middleName',
                    required: false,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60)
                },
                {
                    id: 'profile_surname',
                    initialValue: profileData.legalSurname,
                    label: 'Surname',
                    name: 'legalSurname',
                    required: true,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60)
                },
                {
                    id: 'profile_preferredForename',
                    initialValue: profileData.chosenName,
                    label: 'Preferred Forename',
                    name: 'chosenName',
                    required: false,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60)
                },
                {
                    id: 'profile_preferredSurname',
                    initialValue: profileData.surname,
                    label: 'Preferred Surname',
                    name: 'surname',
                    required: true,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60)
                },
                {
                    id: 'profile_dateOfBirth',
                    initialValue: dateOfBirth && dateOfBirth.year() !== 1 ? dateOfBirth : null,
                    label: 'Date Of Birth',
                    name: 'dateOfBirth',
                    type: 'date',
                    required: true,
                    inputType: 'date',
                    disabled: !(isEditMode && !userHasParentRole),
                    minDate: moment(new Date('1970-01-01')),
                    nullableOnEmpty: true
                },
                {
                    id: 'profile_gender',
                    initialValue: profileData.gender,
                    label: 'Sex',
                    name: 'gender',
                    type: 'select',
                    select: true,
                    required: false,
                    disabled: !(isEditMode && !userHasParentRole),
                    nullableOnEmpty: true,
                    options: [{value: 'M', title: 'Male'}, {value: 'F', title: 'Female'}],
                },
                {
                    id: 'profile_email',
                    initialValue: profileData.email,
                    label: 'Email',
                    name: 'email',
                    required: false,
                    disabled: !(isEditMode && !userHasParentRole),
                    validate: validateEmail,
                    nullableOnEmpty: true
                }
            ]
            : [];
    }, [profileData, isEditMode]);

    const isAnyAddressFieldInProfileData = () => {
        const addressKeys = ['town', 'postcode', 'houseNumber', 'apartment'];
        return addressKeys.some((key) => key in profileData);
    };

    const canRenderRegistrationInformation = true;
    const canRenderAddress = isAnyAddressFieldInProfileData();
    const shouldRenderDetailsColumn = canRenderRegistrationInformation || canRenderAddress;
    const canViewFullApplicantRegistrationInformation = profileData.canViewApplicantRegistrationInformation !== false &&
        canViewPermissionModule('ApplicantRegistrationInformation');

    // Its own section with its own permission, because a school that hides Registration Information
    // for its applicants still wants the admissions categories in view, and the other way round.
    const canRenderAdmissionCategories = canViewPermissionModule('ApplicantAdmissionCategories');
    // Saving a child's categories needs Edit on the same row, so a person with View reads them only.
    const canEditAdmissionCategories = user.permissions.includes('Permission.ApplicantAdmissionCategories.Edit');

    const educationInfoTextFields = useMemo(() =>
        profileData
            ? [
                selectedProfile.isApplicant
                    ? {
                        id: 'profile_intakeGroup',
                        label: 'Intake Group',
                        name: 'intakeId',
                        type: 'select',
                        select: true,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.intakeId,
                        options: yearGroups.map(element => ({value: element.id, title: element.name})),
                        nullableOnEmpty: true
                    }
                    : {
                        id: 'profile_yearGroup',
                        label: 'Year Group',
                        name: 'yearOfEntryId',
                        type: 'select',
                        select: true,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.yearOfEntryId,
                        options: yearGroups.map(element => ({value: element.id, title: element.name})),
                        nullableOnEmpty: true
                    },
                ...(selectedProfile.personType === PROFILE_TYPE.ENQUIRY
                    ? [
                        {
                            id: 'profile_yearOfEntry',
                            label: 'Year of Entry',
                            name: 'enquiryAdmissionYear',
                            disabled: true,
                            initialValue: profileData.enquiryAdmissionYear
                        },
                        {
                            id: 'profile_enquirySeason',
                            label: 'Season',
                            name: 'enquirySeason',
                            disabled: true,
                            initialValue: profileData.enquirySeason
                        }
                    ]
                    : []),
                ...[
                    {
                        id: 'profile_form',
                        label: 'Form',
                        name: 'formId',
                        type: 'select',
                        select: true,
                        required: false,
                        parentEdit: false,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.formId,
                        options: formGroups.map(element => ({value: element.id, title: element.name})),
                        nullableOnEmpty: true
                    },
                    ...(currentSchool.hideHouseGroups
                        ? []
                        : [
                            {
                                id: 'profile_house',
                                label: 'House',
                                name: 'houseId',
                                type: 'select',
                                select: true,
                                required: false,
                                parentEdit: false,
                                disabled: !(isEditMode && !userHasParentRole),
                                initialValue: profileData.houseId,
                                options: houseGroups.map(element => ({value: element.id, title: element.name})),
                                nullableOnEmpty: true
                            }
                        ]),
                    ...(currentSchool.hideBoarderGroups
                        ? []
                        : [
                            {
                                id: 'profile_boarderStatus',
                                label: 'Boarder Status',
                                name: 'boarderId',
                                type: 'select',
                                select: true,
                                required: false,
                                parentEdit: false,
                                disabled: !(isEditMode && !userHasParentRole),
                                initialValue: profileData.boarderId,
                                options: boarderGroups.map(element => ({value: element.id, title: element.name})),
                                nullableOnEmpty: true
                            }
                        ]),
                    {
                        id: 'previousSchoolName',
                        label: 'Previous School Name',
                        name: 'previousSchoolName',
                        required: false,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.previousSchoolName
                    },
                    {
                        id: 'previousSchoolEmail',
                        label: 'Previous School Email',
                        name: 'previousSchoolEmail',
                        required: false,
                        validate: validateEmail,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.previousSchoolEmail
                    },
                    {
                        id: 'profile_upn',
                        label: 'UPN',
                        name: 'upn',
                        required: false,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.upn,
                        validate: (value) => ({
                            isValid: isValidUPN(value),
                            message: 'UPN\'s must use the format Annnnnnnnnnnn. The letters I,O and S are not allowed. Temporary UPN\'s have a trailing letter.'
                        }),
                    }
                ]
            ]
            : [], [profileData, isEditMode, currentSchool, yearGroups, formGroups, houseGroups, boarderGroups, userHasParentRole]);

    const {
        fields: studentInfoFields,
        getFieldValues: getStudentFieldValues
    } = useTextFields(studentInfoTextFields);
    const {
        fields: educationInfoFields,
        getFieldValues: getEducationFieldValues
    } = useTextFields(educationInfoTextFields);
    const visibleEducationInfoFields = useMemo(() => {
        if (!selectedProfile.isApplicant || canViewFullApplicantRegistrationInformation) {
            return educationInfoFields;
        }

        return Object.fromEntries(
            Object.entries(educationInfoFields)
                .filter(([, field]) => ['previousSchoolName', 'previousSchoolEmail'].includes(field.textFieldProps.name))
        );
    }, [educationInfoFields, selectedProfile.isApplicant, canViewFullApplicantRegistrationInformation]);

    const isAddressValid = useMemo(() => {
        if (!selectedAddress) return true;

        const fields = ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode'];
        const filled = fields.filter(f => selectedAddress?.[f] && selectedAddress[f].toString().trim() !== '');

        if (filled.length === 0) return true;

        const hasPostcode = Boolean(selectedAddress?.postcode && selectedAddress.postcode.toString().trim() !== '');
        const hasTownOrStreet = Boolean(
            (selectedAddress?.town && selectedAddress.town.toString().trim() !== '') ||
            (selectedAddress?.street && selectedAddress.street.toString().trim() !== '')
        );

        return hasPostcode && hasTownOrStreet;
    }, [selectedAddress]);

    const areAllTextFieldsValid =
        checkTextFieldsAreValid(studentInfoFields) &&
        checkTextFieldsAreValid(educationInfoFields);

    const isFormValid = areAllTextFieldsValid && isAddressValid;

    useEffect(() => {
        dispatch(retrieveBasicInfo({ studentId, isParent: userHasParentRole })).then(() => {
            setAddressComponentKey(prev => prev + 1);
        });
        dispatch(retrieveProfile({ studentId }))
            .then(res => {
                dispatch(retrieveGroupsForBasicDetails({
                    isApplicant: res.payload?.personType === PROFILE_TYPE.APPLICATION
                }));
            });
    }, [dispatch, studentId, userHasParentRole]);

    useEffect(() => {
        if (selectedProfile.personType !== PROFILE_TYPE.ENQUIRY && selectedProfile.personType !== PROFILE_TYPE.NONE) {
            dispatch(retrieveCountries());
            dispatch(retrieveContactRelations());
        }
    }, [selectedProfile.personType]);

    useEffect(() => {
        if (profileData) {
            const currentAddressString = JSON.stringify(originalAddress);
            const newAddressString = JSON.stringify(profileAddress);

            if (currentAddressString !== newAddressString) {
                setOriginalAddress(profileAddress);
                if (!isEditMode) {
                    setSelectedAddress(profileAddress);
                }
            }
        }
    }, [profileData, profileAddress, isEditMode, originalAddress]);

    useEffect(() => {
        showNotification(updatingSuccessful);
        if (updatingSuccessful) {
            dispatch(resetUpdatingStatus());
            dispatch(retrieveBasicInfo({studentId: selectedProfile.id, isParent: userHasParentRole}));
            dispatch(retrieveProfile({studentId: selectedProfile.id}));
            setIsEditMode(false);
            setPendingPhoto(null);
            setAddressUpdateMode('all');
            setAddressComponentKey(prev => prev + 1);
        }

        return () => {
            dispatch(resetUpdatingStatus());
        };
    }, [updatingSuccessful]);

    const showNotification = success => success !== undefined && dispatch({
        true: () => showSnackbar({
            message: 'Basic student information updated successfully', severity: 'success'
        }),
        false: () => showSnackbar({
            message: 'Failed to update basic student information', severity: 'error'
        })
    }[success]());

    const handleSave = () => {
        const updateModeText = addressUpdateMode === 'all'
            ? 'This will update the address for all family members who share the same current address.'
            : 'This will only update the address for this student.';

        confirm({
            title: 'Confirmation',
            description: (
                <Box>
                    <Typography>
                        {`I confirm that the information in the basic profile section of ${selectedProfile.name} ${selectedProfile.surname} student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`}
                    </Typography>
                    <Typography sx={{mt: 2, fontWeight: 'bold'}}>
                        {updateModeText}
                    </Typography>
                </Box>
            ),
            confirmationText: 'Confirm'
        })
            .then(() => {
                const {photo: _photo, ...profileDetails} = profileData;
                const studentDetails = {
                    ...profileDetails,
                    ...getStudentFieldValues(),
                    ...getEducationFieldValues(),
                    ...selectedAddress,
                    updateAllLinkedAddresses: addressUpdateMode === 'all',
                    ...(pendingPhoto !== null ? {photo: pendingPhoto} : {}),
                };

                if (userHasParentRole) {
                    dispatch(updateStudentByParent({
                        studentId: selectedProfile.id,
                        id: studentDetails.id,
                        studentDetails
                    }));
                } else {
                    dispatch(updateBasicInfo({
                        studentId: selectedProfile.id,
                        id: studentDetails.id,
                        studentDetails
                    }));
                }
            })
            .catch(() => {
            });
    };

    const handleConfirm = () => {
        confirm({
            title: 'Confirmation',
            description: `I confirm that the information in the basic profile section of ${selectedProfile.name} ${selectedProfile.surname} student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
            confirmationText: 'Confirm'
        })
            .then(() => {
                dispatch(confirmProfile({
                    studentId: selectedProfile.id,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES.Basic
                }));
            })
            .catch(() => {
            });
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setPendingPhoto(null);
        setSelectedAddress(originalAddress);
        setAddressUpdateMode('all');
        setAddressComponentKey(prev => prev + 1);
    };

    const handleApplicantPhotoSelect = (photo) => {
        setPendingPhoto(photo);
    };

    const renderProfileStatus = () => {
        const isEnquiry = selectedProfile.personType === PROFILE_TYPE.ENQUIRY;

        return selectedProfile.personType === PROFILE_TYPE.APPLICATION || isEnquiry
            ? <ProfileStatus
                status={isEnquiry ? 'Enquiry' : selectedProfile.status}
                statusCategory={isEnquiry ? 'Enquiry' : selectedProfile.statusCategory}
                isStudentProfile/>
            : null;
    };

    return (<>
        {profileData && <>
            <Box
                display="flex"
                flexDirection={{
                    xs: 'column',
                    sm: 'column',
                    md: 'row',
                    lg: 'row',
                    xl: 'row'
                }}
                gap={2}
            >
                <Box flex={1}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems={'start'}
                            >
                                {selectedProfile.isApplicant
                                    ? (
                                        <ApplicantPhotoUpload
                                            photo={pendingPhoto ?? selectedProfile.photo}
                                            alt={`${selectedProfile.name} ${selectedProfile.surname}`}
                                            canUpload={!readonly && isEditMode}
                                            onPhotoSelect={handleApplicantPhotoSelect}
                                        />
                                    )
                                    : (
                                        <Avatar
                                            variant="square"
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                marginBottom: 2
                                            }}
                                            alt={`${selectedProfile.name} ${selectedProfile.surname}`}
                                            src={selectedProfile.photo && `data:image/jpeg;base64,${selectedProfile.photo}`}
                                        />
                                    )}
                                {renderProfileStatus()}
                            </Box>
                            <EditableFields fields={studentInfoFields} edit={isEditMode}/>
                        </CardContent>
                    </Card>
                </Box>
                {shouldRenderDetailsColumn && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        flex={2}
                        gap={2}
                    >
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold">Registration Information</Typography>
                                <EditableFields
                                    fields={selectedProfile.personType === PROFILE_TYPE.APPLICATION
                                        ? visibleEducationInfoFields
                                        : educationInfoFields}
                                    edit={isEditMode && (!selectedProfile.isApplicant || canViewFullApplicantRegistrationInformation)}
                                />
                            </CardContent>
                        </Card>

                        {canRenderAdmissionCategories && (
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" fontWeight="bold">
                                        Admissions Categories
                                    </Typography>
                                    <AdmissionCategories
                                        studentId={studentId}
                                        selected={profileData.admissionCategories}
                                        readonly={!isEditMode || userHasParentRole || !canEditAdmissionCategories}
                                    />
                                </CardContent>
                            </Card>
                        )}

                        {canRenderAddress && (<Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold">Home Address</Typography>
                                <Box sx={{mt: 1}}>
                                    <AdmicityAddressLookup
                                        key={addressComponentKey}
                                        onAddressSelect={(address) => {
                                            setSelectedAddress(address);
                                        }}
                                        readonly={!isEditMode}
                                        initialValue={selectedAddress || profileAddress}
                                        defaultCountry={process.env.REACT_APP_DEFAULT_COUNTRY}
                                    />
                                    {isEditMode && selectedAddress && !isAddressValid && (
                                        <Typography variant="body2" color="error" sx={{mt: 1}}>
                                            Address must have a postcode AND either a town or street
                                        </Typography>
                                    )}

                                    {isEditMode && (
                                        <>
                                            <Divider sx={{my: 3}} />
                                            <Typography variant="h6" sx={{mb: 2}}>
                                                Address Update Options
                                            </Typography>
                                            <FormControl component="fieldset">
                                                <RadioGroup
                                                    value={addressUpdateMode}
                                                    onChange={(e) => setAddressUpdateMode(e.target.value)}
                                                >
                                                    <FormControlLabel
                                                        value="current"
                                                        control={<Radio />}
                                                        label={
                                                            <Box>
                                                                <Typography variant="body1">
                                                                    Update address for this student only
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Only this student&apos;s address will be updated
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                    <FormControlLabel
                                                        value="all"
                                                        control={<Radio />}
                                                        label={
                                                            <Box>
                                                                <Typography variant="body1">
                                                                    Update address for all family members
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Update address for all family members who share the same current address
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>)}
                    </Box>
                )}
            </Box>
            {
                selectedProfile.personType !== PROFILE_TYPE.ENQUIRY && selectedProfile.personType !== PROFILE_TYPE.NONE
                    ? <ActionButtonsBox>
                        {
                            profileData.editedAt && profileData.editedBy && <Typography mt={1}>Confirmed
                                at {formatDate(profileData.editedAt)} by {profileData.editedBy}</Typography>
                        }
                        {isEditMode
                            ? <>
                                <Button
                                    sx={{width: 150}}
                                    variant="outlined"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    sx={{width: 150}}
                                    disabled={!isFormValid}
                                    variant="contained"
                                    onClick={handleSave}
                                >
                                    Save
                                </Button>
                            </>
                            : <>
                                {!profileData.editedAt &&
                                    <Button variant="contained" onClick={handleConfirm}>Confirm Personal Details</Button>}
                                {!readonly &&
                                    <Button variant="contained"
                                            sx={{width: 150}}
                                            onClick={() => setIsEditMode(true)}>
                                        Edit
                                    </Button>}
                            </>}
                    </ActionButtonsBox>
                    : null
            }
        </>}
    </>);
};

BasicInfo.propTypes = {
    readonly: PropTypes.bool
};

export default BasicInfo;
